import styled from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { BAR_COLORS, COLORS, SPACE, STATUS_COLORS } from '@/nova/theme/tokens';

import type { LayerStageStyle } from '../../layer-transforms';

/* The kit layer editor: a stage on the left (the shape's render at its own aspect, the base image with every layer
   positioned over it) and the layer panel on the right (the stack, then the selected layer's controls). Desktop
   only. The shared text pieces are the banner-kit composer's, so the two sheets read as one system. */

export {
  Foot,
  FootActions,
  FootStatus,
  FootText,
  Hint,
  Section,
  SectionTitle,
  Spinner,
} from '../BannerKitComposer/BannerKitComposer.styles';

export const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: ${SPACE[20]};
  align-items: start;
`;

/** Measured for the stage size: the stage fits inside it at the render's aspect. */
export const StageWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 160px;
  padding: ${SPACE[12]};
  border-radius: 12px;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
`;

/** The render at stage scale. Its size is set per frame through `style` (a class per size would pile up). */
export const Stage = styled.div.attrs<{ $w: number; $h: number }>((p) => ({
  style: { width: `${p.$w}px`, height: `${p.$h}px` },
}))<{ $w: number; $h: number }>`
  position: relative;
  overflow: hidden;
  flex: none;
  user-select: none;
  touch-action: none;
  border-radius: 4px;
  background: ${COLORS.surfaceFaint};

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

export const StageBase = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: fill;
  display: block;
  pointer-events: none;
`;

/** One layer PNG on the stage. Its placement changes on every drag frame, so it goes through `style`. */
export const LayerImg = styled.img.attrs<{ $place: LayerStageStyle; $z: number; $opacity: number }>((p) => ({
  style: {
    left: `${p.$place.left}px`,
    top: `${p.$place.top}px`,
    width: `${p.$place.width}px`,
    height: `${p.$place.height}px`,
    transform: p.$place.transform,
    transformOrigin: p.$place.transformOrigin,
    zIndex: p.$z,
    opacity: p.$opacity,
    // An invisible (hidden, unselected) layer must not catch the pointer meant for the layers under it.
    pointerEvents: p.$opacity > 0 ? ('auto' as const) : ('none' as const),
  },
}))<{ $place: LayerStageStyle; $z: number; $opacity: number; $selected: boolean; $grabbable: boolean }>`
  position: absolute;
  display: block;
  max-width: none;
  cursor: ${(p) => (p.$grabbable ? 'grab' : 'default')};
  user-select: none;
  -webkit-user-drag: none;
  outline: ${(p) => (p.$selected ? `1px dashed ${COLORS.accent}` : 'none')};
  outline-offset: 0;

  &:active {
    cursor: ${(p) => (p.$grabbable ? 'grabbing' : 'default')};
  }
`;

/** Shown over the stage while the base image loads (the stage needs its size). */
export const StageNote = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

/* ── Panel ── */

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[20]};
  min-width: 0;
`;

export const ControlRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACE[8]};
`;

export const ControlLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textMuted};
`;

export const Range = styled.input.attrs({ type: 'range' as const })`
  width: 100%;
  accent-color: ${COLORS.accent};
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

/* ── Layer list ── */

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  max-height: 280px;
  overflow-y: auto;
`;

export const Row = styled.li<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACE[4]};
  padding: ${SPACE[4]} ${SPACE[4]} ${SPACE[4]} ${SPACE[8]};
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$selected ? COLORS.accent : COLORS.hairline)};
  background: ${(p) => (p.$selected ? COLORS.hoverWash : 'transparent')};
`;

export const RowMain = styled.button`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACE[2]};
  padding: ${SPACE[4]} 0;
  border: 0;
  background: none;
  text-align: left;
  cursor: pointer;
  color: inherit;

  &:disabled {
    cursor: default;
  }
`;

export const RowRole = styled(Text).attrs({ tag: 'span' as const })<{ $hidden: boolean }>`
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.$hidden ? COLORS.textFaint : COLORS.text)};
`;

export const RowName = styled(Text).attrs({ tag: 'span' as const })`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  color: ${COLORS.textFaint};
`;

export const RowActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[2]};
  flex: none;
`;

export const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: none;
  color: ${COLORS.textMuted};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${COLORS.hoverWash};
    color: ${COLORS.text};
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

/** A text layer whose words did not read back as intended (its row's tooltip says why). Never wraps. */
export const CheckBadge = styled(Text).attrs({ tag: 'span' as const })`
  flex: none;
  white-space: nowrap;
  padding: 0 ${SPACE[8]};
  border-radius: 999px;
  border: 1px solid ${BAR_COLORS.warning};
  color: ${BAR_COLORS.warning};
  font-size: 10px;
  font-weight: 600;
`;

/* ── Size grid additions (the tile's layer state) ── */

/** Over a tile while its shape is being separated into layers: the render stays visible under it. */
export const TileBusy = styled.div`
  position: absolute;
  left: ${SPACE[4]};
  bottom: ${SPACE[4]};
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  padding: ${SPACE[2]} ${SPACE[8]};
  border-radius: 999px;
  background: ${COLORS.overlaySoft};
`;

export const TileBusyText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  color: ${COLORS.text};
`;

/** Why the layers of a tile could not be separated, under its caption. */
export const TileIssue = styled(Text).attrs({ tag: 'span' as const })`
  flex-basis: 100%;
  max-width: 100%;
  font-size: 11px;
  line-height: 1.3;
  color: ${STATUS_COLORS.negative};
`;
